import { GaLowLevelEffect, type GaVueComponent } from "@/common/vueUtils";
import type { Store } from "@/common/storeUtils";
import type { ApiResult } from "@utils/httpClientCore";
import { getErrorMessage } from "@utils/httpClientCsrf";
import { hasProp } from "@utils/assertion";
import type { DeepReadonly } from "vue";
import { GaAlert } from "@/components/general/GaAlert";
import type { TranslationFn } from "@utils/vue-migration/translationUtils";

// Should mostly be handled the same as pending for the user, but we need to be able to distinguish between the two states
// (not yet started the asynchronous call vs. already started the asynchronous side effect but not yet finished).
export type Uninitialised = {
  kind: "UNINITIALISED";
};

export type Pending = {
  kind: "PENDING";
};

export type Fulfilled<T> = {
  kind: "FULFILLED";
  value: T;
};

export type Rejected = {
  kind: "REJECTED";
  errorCode: string | null;
  errorMessage: string | null;
};

export type Future<T> = Uninitialised | Pending | Fulfilled<T> | Rejected;

export type SafeFuture<T> = Uninitialised | Pending | Fulfilled<T>;

export function initializeFuture(): Uninitialised {
  return { kind: "UNINITIALISED" };
}

export function isPending<T>(future: Future<T>): future is Uninitialised | Pending {
  return future.kind === "PENDING" || future.kind === "UNINITIALISED";
}

/**
 * @deprecated use [gaDesign/src/common/dataHandling/DataLoader.tsx] instead
 */
export function GaSuspense<T>(props: { store: Store<Future<T>>; fetch: () => Promise<ApiResult<T>>; render: (data: Future<DeepReadonly<T>>) => GaVueComponent }): GaVueComponent {
  return (
    <GaLowLevelEffect
      sideEffect={async () => {
        if (props.store.get().kind === "PENDING") {
          // The Future is already pending, do nothing.
          // This is important so that we can have multiple suspense components that are waiting for the same future, but running the side effect only once.
          return;
        }

        props.store.set({ kind: "PENDING" });

        try {
          const result = await props.fetch();
          if (result.kind === "success") {
            if (hasProp(result, "data")) {
              props.store.set({ kind: "FULFILLED", value: result.data });
            } else {
              props.store.set({ kind: "REJECTED", errorCode: null, errorMessage: null });
            }
          } else {
            props.store.set({ kind: "REJECTED", errorCode: result.errorCode, errorMessage: result.errorMessage });
          }
        } catch (_) {
          props.store.set({ kind: "REJECTED", errorCode: null, errorMessage: null });
        }
      }}
      content={props.render(props.store.get())}
    />
  );
}

export type Unsafe<T, U extends Uninitialised | Pending | Fulfilled<T>> = Rejected | U;

/**
 * @deprecated use [gaDesign/src/common/dataHandling/DataLoader.tsx] instead
 */
export function GaHandleRejected<T, U extends Uninitialised | Pending | Fulfilled<T>>(props: {
  unsafeFuture: Unsafe<T, U>;
  renderRejected?: (error: Rejected) => GaVueComponent;
  renderSafe: (safeFuture: U) => GaVueComponent;
  ctx: { t: TranslationFn };
}): GaVueComponent {
  function ErrorComponent(p: { e: Rejected }): GaVueComponent {
    if (props.renderRejected !== undefined) {
      return props.renderRejected(p.e);
    }
    return <GaAlert variant="danger">{getErrorMessage(p.e, props.ctx.t)}</GaAlert>;
  }

  const unpacked: { kind: "REJECTED"; error: Rejected } | { kind: "SAFE"; safe: U } = props.unsafeFuture.kind === "REJECTED" ? { kind: "REJECTED", error: props.unsafeFuture } : { kind: "SAFE", safe: props.unsafeFuture };

  return unpacked.kind === "REJECTED" ? <ErrorComponent e={unpacked.error} /> : props.renderSafe(unpacked.safe);
}

export type MaybePending<T, U extends Fulfilled<T> | Rejected> = Uninitialised | Pending | U;

/**
 * @deprecated use [gaDesign/src/common/dataHandling/future.tsx] instead
 */
export function GaHandlePending<T, U extends Fulfilled<T> | Rejected>(props: { future: MaybePending<T, U>; renderPending: () => GaVueComponent; renderResult: (result: U) => GaVueComponent }): GaVueComponent {
  if (isPending(props.future)) {
    return props.renderPending();
  }
  return props.renderResult(props.future);
}

export type MaybeFulfilled<T, U extends Uninitialised | Pending | Rejected> = Fulfilled<T> | U;

export function mapFuture<T, U, V extends Uninitialised | Pending | Rejected>(future: MaybeFulfilled<T, V>, transform: (safeData: T) => U): MaybeFulfilled<U, V> {
  if (future.kind === "FULFILLED") {
    return { kind: "FULFILLED", value: transform(future.value) };
  }
  return future;
}
